import React, { useEffect, useRef, useState } from 'react';
import styles from './PaymentSuccessModal.module.scss';
import Lottie from 'react-lottie';
import useLottieAnimation from '@cp/src/hooks/useLottieAnimation';
import { Typography } from '@navi/web-ui/lib/primitives';
import TadaIcon from '@cp/src/assets/icons/tada-icon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { debounce } from '@navi/web-ui/lib/utils';
import {
  closePaymentDoneModal,
  triggerPaymentDoneModal
} from '../Notifications/NotificationsActions';
import { formatINR } from '@cp/src/utils/commonUtils';
import useDocumentActive from '@cp/src/hooks/useDocumentActive';
import { PaymentDoneData } from '../Notifications/interfaces';

const PaymentSuccessModal: React.FC = () => {
  const coinsAnimation = useLottieAnimation(
    () => import('@cp/src/assets/json/coins-lottie.json'),
    'coins-lottie'
  );
  const dispatch = useDispatch();
  const { paymentDoneModalData, showPaymentDoneModal } = useSelector(
    (state: RootState) => state?.notification
  );
  const [customerNames, setCustomerNames] = useState<string>('');
  const [totalAmount, setTotalAmount] = useState<string>('0');
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const isDocumentVisible = useDocumentActive();
  const animationTimeoutRef = useRef<any>();

  const closeModal = () => {
    dispatch(closePaymentDoneModal());
  };

  const calculateData = (paymentDoneModalData: Array<PaymentDoneData>) => {
    const totalAmount = paymentDoneModalData.reduce((acc, item) => acc + item.amount, 0);
    let customerNames = '';
    if (paymentDoneModalData.length > 3) {
      customerNames =
        paymentDoneModalData[0].customerName +
        ', ' +
        paymentDoneModalData[1].customerName +
        ', and ' +
        (paymentDoneModalData.length - 2) +
        ' others';
    } else {
      customerNames = paymentDoneModalData.map(item => item.customerName).join(', ');
      const replacer = paymentDoneModalData.length > 2 ? ', and$1' : ' and$1';
      customerNames = customerNames.replace(/,([^,]*)$/, replacer); // replacing last comma with "and"
    }
    setTotalAmount(formatINR(totalAmount, 0, 2));
    setCustomerNames(customerNames);
    if (paymentDoneModalData.length > 0) {
      dispatch(triggerPaymentDoneModal(paymentDoneModalData.map(item => item.id)));
      debouncedCloseModal.current();
    }
  };

  const debouncedCalculateData = useRef(debounce(calculateData, 3000));
  const debouncedCloseModal = useRef(debounce(closeModal, 6000));

  const coinsRainingdefaultOptions = {
    loop: false,
    autoplay: true,
    animationData: coinsAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice'
    }
  };

  const handlePaymentDataChange = () => {
    if (paymentDoneModalData.filter(item => !item.isRead).length == 0) {
      return;
    }
    if (paymentDoneModalData.length > 0 && showPaymentDoneModal == true) {
      // Modal is already open. Calculate new values without debounce.
      calculateData(paymentDoneModalData);
      return;
    }
    // Modal is closed. Let's debounce.
    debouncedCalculateData.current(paymentDoneModalData);
  };

  useEffect(() => {
    if (isDocumentVisible) {
      // tab is focused. check if there are any pending requests.
      handlePaymentDataChange();
    }
  }, [isDocumentVisible]);

  useEffect(() => {
    if (isDocumentVisible) {
      // tab is focused. Call the method directly.
      handlePaymentDataChange();
    }
  }, [paymentDoneModalData]);

  const onModalClick = (e: React.MouseEvent<HTMLElement>) => {
    closeModal();
  };

  useEffect(() => {
    clearTimeout(animationTimeoutRef.current);
    if (showPaymentDoneModal) {
      animationTimeoutRef.current = setTimeout(() => setIsAnimating(showPaymentDoneModal), 500);
      return;
    }
    setIsAnimating(showPaymentDoneModal);
  }, [showPaymentDoneModal]);

  return (
    <div
      className={`${styles.wrapper} ${showPaymentDoneModal ? styles.open : ''}`}
      onClick={onModalClick}
    >
      <div className={styles.lottieContent}>
        {isAnimating && coinsAnimation && (
          <Lottie options={coinsRainingdefaultOptions} isStopped={!isAnimating} />
        )}
      </div>
      <div
        className={`${styles.content} ${showPaymentDoneModal ? styles.open : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.header}>
          <TadaIcon className={styles.flip} />
          <Typography variant="h4" color={'white'} className={styles.text}>
            Congratulations!
          </Typography>
          <TadaIcon />
        </div>
        <div className={styles.body}>
          <Typography variant="p3" color={'#585757'} style={{ lineHeight: '17px' }}>
            <b>{customerNames}</b>
            <br />
            made a {paymentDoneModalData?.length > 1 ? 'total' : ''} payment of
          </Typography>
          <Typography variant="h1" color={'#33C85D'} style={{ fontWeight: 700, marginTop: '5px' }}>
            {totalAmount}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;
